package no.vareflow.web

import no.vareflow.app.domain.*

/** Live totals of one Hovedvaregruppe in the open month. */
class CategoryTotals(val category: String, val counted: Int, val total: Int, val value: Double)

/** Live totals of an open month (Utkast/Aktiv), computed from the count entries as they are saved. */
class LiveTotals(
    val counted: Int,
    val total: Int,
    val value: Double,
    val categories: List<CategoryTotals>,
    /** Value per leverandør; products without a leverandør are left out. */
    val suppliers: List<Pair<String, Double>>
) {
    val missing: Int get() = (total - counted).coerceAtLeast(0)
    val percent: Int get() = if (total == 0) 0 else counted * 100 / total
}

/** Months still being worked on, oldest first: these are the month tabs on Oversikt. */
fun WebApp.openSessions(): List<InventorySession> =
    core.inventorySessions
        .filter { it.status == InventoryStatus.DRAFT || it.status == InventoryStatus.ACTIVE }
        .sortedWith(compareBy({ it.yearMonth }, { it.locationName }))

/** The open month shown on Oversikt and Analyse: the selected one if it is still open, else the first open one. */
fun WebApp.shownOpenSession(): InventorySession? {
    val open = openSessions()
    val current = core.currentInventorySession()
    return if (current != null && open.any { it.id == current.id }) current
    else open.firstOrNull { it.status == InventoryStatus.ACTIVE } ?: open.firstOrNull()
}

fun WebApp.seesAllCategories(user: UserAccount): Boolean =
    user.role == Role.ADMIN || AccessRules.hasPermission(user, Permission.INVENTORY_VIEW_OTHER)

/** Live totals for [session]; employees only see their assigned Hovedvaregrupper. */
fun WebApp.liveTotals(session: InventorySession, user: UserAccount): LiveTotals {
    val seesAll = seesAllCategories(user)
    val assigned = core.assignmentsForSession(session.id).filter { user.id in it.assigneeIds }.map { it.category }.toSet()
    val categories = core.orderedCategories().filter { seesAll || it in assigned }
    val departments = core.departmentsForSession(session.id)
    val supplierValue = linkedMapOf<String, Double>()
    val perCategory = categories.map { category ->
        var value = 0.0
        core.products.filter { it.category == category }.forEach { product ->
            departments.forEach { department ->
                val quantity = core.entry(session.id, product.id, department.id)?.quantity
                if (quantity != null) {
                    val lineValue = quantity * product.pricePerUnit
                    value += lineValue
                    val supplier = product.supplier.trim()
                    if (supplier.isNotEmpty()) supplierValue[supplier] = (supplierValue[supplier] ?: 0.0) + lineValue
                }
            }
        }
        CategoryTotals(category, core.countedCount(session.id, category), core.totalToCount(session.id, category), value)
    }
    return LiveTotals(
        counted = perCategory.sumOf { it.counted },
        total = perCategory.sumOf { it.total },
        value = perCategory.sumOf { it.value },
        categories = perCategory,
        suppliers = supplierValue.toList().sortedByDescending { it.second }
    )
}

/** "+4,2 %" / "−3,0 %" against the previous month, or null when there is nothing to compare with. */
fun changeText(current: Double, previous: Double?): String? {
    if (previous == null || previous == 0.0) return null
    val change = (current - previous) / previous * 100
    val rounded = kotlin.math.round(kotlin.math.abs(change) * 10) / 10
    val text = rounded.toString().replace('.', ',').let { if (',' in it) it else "$it,0" }
    return (if (change >= 0) "+" else "−") + text + " %"
}
